// Local diagnostic evidence only. No URLs, account data, credentials or usage events.
import { navigationTraceId } from "./httpTiming";
type Startup = { id: number; start: number; phases: Set<string> };
let current: Startup = { id: 1, start: 0, phases: new Set() };

export function beginAppSelection() {
  current = { id: current.id + 1, start: performance.now(), phases: new Set() };
  recordStartup(current, "selection");
}

export function appStartup() { return current; }

export function recordStartup(startup: Startup, phase: string, at = performance.now(), telemetry?: unknown) {
  if (startup.phases.has(phase)) return;
  startup.phases.add(phase);
  const detail = { trace_id: navigationTraceId, attempt: startup.id, phase, elapsed_ms: Math.max(0, at - startup.start), telemetry };
  performance.mark?.(`ato:startup:${startup.id}:${phase}`, { startTime: at, detail });
  window.dispatchEvent(new CustomEvent("ato:startup-timing", { detail }));
  console.debug("[ato:startup-timing]", JSON.stringify(detail));
}

/** Copy only the bounded numeric telemetry schema; untrusted apps cannot log arbitrary content. */
export function startupTelemetry(value: unknown) {
  if (!value || typeof value !== "object") return undefined;
  const input = value as Record<string, unknown>;
  const numbers = (source: unknown, keys: string[]) => {
    const output: Record<string, number> = {};
    if (!source || typeof source !== "object") return output;
    for (const key of keys) {
      const number = (source as Record<string, unknown>)[key];
      if (typeof number === "number" && Number.isFinite(number) && number >= 0) output[key] = number;
    }
    return output;
  };
  const resources: Record<string, Record<string, number>> = {};
  if (input.resources && typeof input.resources === "object") {
    for (const kind of ["script", "model", "texture", "other"])
      resources[kind] = numbers((input.resources as Record<string, unknown>)[kind],
        ["count", "transfer_bytes", "decoded_bytes", "cached", "unknown", "max_duration_ms"]);
  }
  const document = input.document && typeof input.document === "object" ? input.document as Record<string, unknown> : {};
  const server = Array.isArray(document.server) ? document.server.slice(0, 40).flatMap(item => {
    if (!item || typeof item.name !== "string" || !/^ato_[a-z_]{1,64}$/.test(item.name) ||
        typeof item.duration !== "number" || !Number.isFinite(item.duration) || item.duration < 0) return [];
    return [{ name: item.name, duration: item.duration,
      ...(/^ato_(trace|request|queries|db_calls)$/.test(item.name) && typeof item.description === "string" &&
        /^[0-9a-f-]{1,36}$/i.test(item.description) ? { description: item.description } : {}) }];
  }) : [];
  return { resources, work: numbers(input.work, ["renderer", "world", "first-frame"]), document: numbers(input.document,
    ["response_start_ms", "response_end_ms", "dom_interactive_ms", "load_ms", "transfer_bytes", "decoded_bytes"]),
    network: numbers(document.network, ["workerStart", "fetchStart", "redirectStart", "redirectEnd", "domainLookupStart", "domainLookupEnd", "connectStart", "connectEnd", "secureConnectionStart", "requestStart", "responseStart", "responseEnd", "duration"]), server };
}
